import { useState } from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { CalendarClock, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/data/services";
import { SITE } from "@/lib/site";
import { SlotPicker } from "./SlotPicker";
import { AttachmentsField } from "./AttachmentsField";

const field = "w-full h-12 px-4 bg-white border border-ink/20 text-ink placeholder:text-slate-400 focus:outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20 transition-[border-color,box-shadow]";
const Label = ({ children, htmlFor }) => <label htmlFor={htmlFor} className="block text-xs font-mono uppercase tracking-[0.15em] font-bold text-slate-700 mb-2">{children}</label>;

const Success = ({ res, onReset }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="booking-success" className="bg-ink text-white p-8 sm:p-12 clip-notch">
    <CheckCircle2 className="h-12 w-12 text-brass" />
    <h3 className="font-wide font-black uppercase text-3xl sm:text-4xl mt-6">Request received.</h3>
    <p className="mt-4 text-slate-300 max-w-lg">Thanks — your {res.kind === "booking" ? "booking request" : "quote request"} is with our team. We'll be in touch shortly to confirm{res.kind === "booking" ? " your arrival window" : ""}.</p>
    <div className="mt-8 grid gap-4 sm:grid-cols-3 border-t border-white/15 pt-6 font-mono text-sm">
      <div><p className="text-slate-400 text-xs">Reference</p><p data-testid="booking-reference" className="text-brass text-lg font-bold">{res.reference}</p></div>
      {res.date && <div><p className="text-slate-400 text-xs">Date</p><p>{res.date}</p></div>}
      {res.time_slot && <div><p className="text-slate-400 text-xs">Window</p><p>{res.time_slot}</p></div>}
    </div>
    <p className="mt-6 text-sm text-slate-300">Urgent? Call <a href={SITE.tel} className="text-brass font-bold">{SITE.phone}</a></p>
    <button onClick={onReset} data-testid="booking-new-request" className="mt-8 h-12 px-6 rounded-full border border-white/30 hover:bg-white hover:text-ink transition-colors font-bold text-sm">Make another request</button>
  </motion.div>
);

const EMPTY = { name: "", phone: "", email: "", suburb: "", address: "", property_type: "House", urgency: "Flexible", message: "" };

export const BookingForm = ({ defaultKind = "booking", defaultService = "", defaultSuburb = "", showToggle = true }) => {
  const [kind, setKind] = useState(defaultKind);
  const [service, setService] = useState(SERVICE_OPTIONS.includes(defaultService) ? defaultService : "");
  const [date, setDate] = useState();
  const [slot, setSlot] = useState("");
  const [f, setF] = useState({ ...EMPTY, suburb: defaultSuburb });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [files, setFiles] = useState([]);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!service) return toast.error("Please choose a service");
    if (kind === "booking" && (!date || !slot)) return toast.error("Please choose a date and time window");
    if (files.some((x) => x.status === "uploading")) return toast.error("Please wait for your files to finish uploading");
    setBusy(true);
    try {
      const attachment_ids = files.filter((x) => x.status === "done" && x.id).map((x) => x.id);
      const payload = { ...f, kind, service, attachment_ids, date: kind === "booking" ? format(date, "yyyy-MM-dd") : null, time_slot: kind === "booking" ? slot : null };
      const { data } = await api.post("/requests", payload);
      setRes(data);
      toast.success(`Request ${data.reference} received`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (res) return <Success res={res} onReset={() => { setRes(null); setF(EMPTY); setDate(undefined); setSlot(""); setFiles([]); }} />;

  return (
    <form onSubmit={submit} data-testid="booking-form" className="space-y-10">
      {showToggle && (
        <div className="grid grid-cols-2 gap-2 p-1.5 bg-bone" role="tablist">
          {[["booking", "Book a time", CalendarClock], ["quote", "Request a quote", FileText]].map(([k, l, I]) => (
            <button type="button" role="tab" aria-selected={kind === k} key={k} onClick={() => setKind(k)} data-testid={`booking-mode-${k}`}
              className={`relative h-14 flex items-center justify-center gap-2 font-bold text-sm sm:text-base transition-colors ${kind === k ? "text-white" : "text-ink hover:text-cobalt"}`}>
              {kind === k && <motion.span layoutId="mode-pill" className="absolute inset-0 bg-ink" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
              <span className="relative flex items-center gap-2"><I className="h-4 w-4" />{l}</span>
            </button>
          ))}
        </div>
      )}
      <fieldset className="space-y-4">
        <legend className="font-wide font-extrabold text-xl mb-4">1. What do you need?</legend>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-1"><Label htmlFor="service">Service</Label>
            <select id="service" value={service} onChange={(e) => setService(e.target.value)} data-testid="booking-service-select" className={field} required>
              <option value="">Select…</option>{SERVICE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </select></div>
          <div><Label htmlFor="ptype">Property</Label>
            <select id="ptype" value={f.property_type} onChange={on("property_type")} data-testid="booking-property-select" className={field}>
              {["House", "Apartment / unit", "Strata building", "Commercial", "Industrial", "New build site"].map((o) => <option key={o}>{o}</option>)}
            </select></div>
          <div><Label htmlFor="urgency">Urgency</Label>
            <select id="urgency" value={f.urgency} onChange={on("urgency")} data-testid="booking-urgency-select" className={field}>
              {["Flexible", "This week", "Urgent"].map((o) => <option key={o}>{o}</option>)}
            </select></div>
        </div>
        <div><Label htmlFor="message">Describe the job</Label>
          <textarea id="message" rows={4} value={f.message} onChange={on("message")} data-testid="booking-message-input" placeholder="E.g. leaking hot water system in the garage, around 10 years old…" className={`${field} h-auto py-3`} /></div>
        <div><Label>Attach photos or files (optional)</Label>
          <p className="text-sm text-slate-600 mb-3">Photos help us understand the job and price it accurately. Builders and homeowners can attach plans here too.</p>
          <AttachmentsField files={files} setFiles={setFiles} /></div>
      </fieldset>
      {kind === "booking" && (
        <fieldset>
          <legend className="font-wide font-extrabold text-xl mb-4">2. Pick a date & arrival window</legend>
          <SlotPicker date={date} setDate={setDate} slot={slot} setSlot={setSlot} />
        </fieldset>
      )}
      <fieldset className="space-y-4">
        <legend className="font-wide font-extrabold text-xl mb-4">{kind === "booking" ? "3" : "2"}. Your details</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label htmlFor="name">Full name</Label><input id="name" required value={f.name} onChange={on("name")} data-testid="booking-name-input" className={field} /></div>
          <div><Label htmlFor="phone">Phone</Label><input id="phone" type="tel" required value={f.phone} onChange={on("phone")} data-testid="booking-phone-input" className={field} /></div>
          <div><Label htmlFor="email">Email</Label><input id="email" type="email" required value={f.email} onChange={on("email")} data-testid="booking-email-input" className={field} /></div>
          <div><Label htmlFor="suburb">Suburb</Label><input id="suburb" required value={f.suburb} onChange={on("suburb")} data-testid="booking-suburb-input" className={field} /></div>
          <div className="sm:col-span-2"><Label htmlFor="address">Street address (optional)</Label><input id="address" value={f.address} onChange={on("address")} data-testid="booking-address-input" className={field} /></div>
        </div>
      </fieldset>
      <button type="submit" disabled={busy} data-testid="booking-submit-button"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-brass text-ink font-bold h-16 px-10 rounded-full hover:bg-ink hover:text-white transition-colors disabled:opacity-60">
        {busy && <Loader2 className="h-5 w-5 animate-spin" />}{kind === "booking" ? "Send booking request" : "Request my quote"}
      </button>
    </form>
  );
};
